import React, { useEffect, useMemo, useRef, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAuth } from "pages/context/AuthContext";

import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("pt-PT");
};

const fmtDateTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString("pt-PT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtMoney = (v) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0,00";
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const getUrgencia = (dataAgendamento) => {
  if (!dataAgendamento) return null;

  const dt = new Date(dataAgendamento);
  if (Number.isNaN(dt.getTime())) return null;

  const hoje = startOfDay(new Date());
  const ag = startOfDay(dt);

  const diffMs = ag.getTime() - hoje.getTime();
  const diffDias = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDias < 0) return "critico";
  if (diffDias <= 1) return "proximo";
  return null;
};

export default function ClientesDebitosAlertasPopper({
  tooltip = "Clientes sem vendas, e com débitos.",
  iconColor = "inherit",
  onOpenInfo,
}) {
  const { userData, temFuncao } = useAuth();

  const isAdminLike = useMemo(
    () =>
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Débitos Clientes"),
    [temFuncao]
  );

  const isComercial = useMemo(() => temFuncao("Comercial"), [temFuncao]);

  const podeVer = isAdminLike || (isComercial && !!userData?.refvendedor);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const [clienteSel, setClienteSel] = useState(null);
  const [vendedorSel, setVendedorSel] = useState(null);
  const [clienteInput, setClienteInput] = useState("");
  const [vendedorInput, setVendedorInput] = useState("");

  const timerRef = useRef(null);

  const carregar = async () => {
    if (!podeVer) {
      setItems([]);
      return;
    }

    setLoading(true);

    const body = isAdminLike
      ? { refvendedor: "", ver_todos: 1 }
      : { refvendedor: userData?.refvendedor || "", ver_todos: 0 };

    const [payload, err] = await handleApi(
      api.post("Comercial/SelClientesDebitosAlertasNavbar", body)
    );

    setLoading(false);

    if (err) {
      setItems([]);
      return;
    }

    const arr = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];

    setItems(arr);
  };

  useEffect(() => {
    if (!podeVer) return undefined;

    carregar();
    timerRef.current = setInterval(carregar, 60 * 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeVer, isAdminLike, userData?.refvendedor]);

  const count = items?.length || 0;

  const handleToggle = (e) => {
    if (open) setAnchorEl(null);
    else setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const existeCritico = (items || []).some(
    (it) => getUrgencia(it?.data_proximo_agendamento) === "critico"
  );

  const existeProximo = (items || []).some(
    (it) => getUrgencia(it?.data_proximo_agendamento) === "proximo"
  );

  const clientesCombo = useMemo(() => {
    const mapa = new Map();

    (Array.isArray(items) ? items : []).forEach((it) => {
      const ref = (it?.refcliente || "").trim();
      if (!ref) return;

      if (!mapa.has(ref)) {
        mapa.set(ref, {
          ref,
          nome: it?.cliente || "",
          pagador: it?.pagador || "",
        });
      }
    });

    return Array.from(mapa.values()).sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
  }, [items]);

  const vendedoresCombo = useMemo(() => {
    const mapa = new Map();

    (Array.isArray(items) ? items : []).forEach((it) => {
      const ref = (it?.refvendedor || "").trim();
      if (!ref) return;

      if (!mapa.has(ref)) {
        mapa.set(ref, {
          ref,
          nome: it?.vendedor || "",
        });
      }
    });

    return Array.from(mapa.values()).sort((a, b) => (a.nome || "").localeCompare(b.nome || ""));
  }, [items]);

  const itemsFiltrados = useMemo(() => {
    let arr = Array.isArray(items) ? [...items] : [];

    if (clienteSel?.ref) {
      arr = arr.filter((it) => (it?.refcliente || "").trim() === clienteSel.ref.trim());
    } else if ((clienteInput || "").trim() !== "") {
      const txt = clienteInput.trim().toLowerCase();
      arr = arr.filter((it) => {
        const nomeCliente = (it?.cliente || "").toLowerCase();
        const refCliente = (it?.refcliente || "").toLowerCase();
        const nomePagador = (it?.pagador || "").toLowerCase();
        const refPagador = (it?.refpagador || "").toLowerCase();

        return (
          nomeCliente.includes(txt) ||
          refCliente.includes(txt) ||
          nomePagador.includes(txt) ||
          refPagador.includes(txt)
        );
      });
    }

    if (vendedorSel?.ref) {
      arr = arr.filter((it) => (it?.refvendedor || "").trim() === vendedorSel.ref.trim());
    } else if ((vendedorInput || "").trim() !== "") {
      const txt = vendedorInput.trim().toLowerCase();
      arr = arr.filter((it) => {
        const nomeVend = (it?.vendedor || "").toLowerCase();
        const refVend = (it?.refvendedor || "").toLowerCase();
        return nomeVend.includes(txt) || refVend.includes(txt);
      });
    }

    return arr;
  }, [items, clienteSel, vendedorSel, clienteInput, vendedorInput]);

  const totalFiltrado = itemsFiltrados?.length || 0;
  const titulo = !totalFiltrado ? "Sem alertas" : `Clientes (${totalFiltrado})`;

  if (!podeVer) return null;

  return (
    <>
      <Tooltip title={tooltip} arrow>
        <IconButton size="small" onClick={handleToggle} sx={{ color: iconColor }}>
          <Badge
            badgeContent={count}
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 11,
                height: 18,
                minWidth: 18,
                backgroundColor: existeCritico ? "#d32f2f" : existeProximo ? "#f57c00" : "#fbc02d",
                color: "#000",
              },
            }}
          >
            <Icon>warning</Icon>
          </Badge>
        </IconButton>
      </Tooltip>

      <Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{ zIndex: 1600 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={10}
            sx={{
              width: 560,
              maxWidth: "95vw",
              borderRadius: 2,
              overflow: "hidden",
              mt: 1,
            }}
          >
            <MDBox
              px={2}
              py={1.5}
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <MDBox display="flex" flexDirection="column" lineHeight={1.2}>
                <MDTypography variant="button" fontWeight="bold">
                  {titulo}
                </MDTypography>

                <MDTypography variant="caption" sx={{ opacity: 0.7, mt: 0.25 }}>
                  Clientes sem vendas há mais de 30 dias e com débitos em atraso.
                </MDTypography>
              </MDBox>

              {loading ? <CircularProgress size={16} /> : null}
            </MDBox>

            <Divider />

            <MDBox px={2} py={1.5}>
              <MDBox
                display="grid"
                gridTemplateColumns={{ xs: "1fr", md: isAdminLike ? "1fr 1fr" : "1fr" }}
                gap={1.25}
              >
                <Autocomplete
                  size="small"
                  options={clientesCombo}
                  value={clienteSel}
                  inputValue={clienteInput}
                  onInputChange={(e, newInputValue) => setClienteInput(newInputValue)}
                  onChange={(e, newValue) => setClienteSel(newValue)}
                  openOnFocus
                  autoHighlight
                  disablePortal
                  getOptionLabel={(opt) =>
                    opt ? `${opt.nome || ""}${opt.ref ? ` (${opt.ref})` : ""}` : ""
                  }
                  isOptionEqualToValue={(opt, val) => (opt?.ref || "") === (val?.ref || "")}
                  renderInput={(params) => (
                    <TextField {...params} label="Cliente" placeholder="Filtrar por cliente" />
                  )}
                />

                {isAdminLike ? (
                  <Autocomplete
                    size="small"
                    options={vendedoresCombo}
                    value={vendedorSel}
                    inputValue={vendedorInput}
                    onInputChange={(e, newInputValue) => setVendedorInput(newInputValue)}
                    onChange={(e, newValue) => setVendedorSel(newValue)}
                    openOnFocus
                    autoHighlight
                    disablePortal
                    getOptionLabel={(opt) =>
                      opt ? `${opt.nome || ""}${opt.ref ? ` (${opt.ref})` : ""}` : ""
                    }
                    isOptionEqualToValue={(opt, val) => (opt?.ref || "") === (val?.ref || "")}
                    renderInput={(params) => (
                      <TextField {...params} label="Vendedor" placeholder="Filtrar por vendedor" />
                    )}
                  />
                ) : null}
              </MDBox>
            </MDBox>

            <Divider />

            <MDBox sx={{ maxHeight: 420, overflow: "auto" }}>
              {!itemsFiltrados?.length ? (
                <MDBox p={2}>
                  <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                    Não há clientes que correspondam aos filtros.
                  </MDTypography>
                </MDBox>
              ) : (
                itemsFiltrados.map((it, idx) => {
                  const urgencia = getUrgencia(it?.data_proximo_agendamento);

                  const corNome =
                    urgencia === "critico"
                      ? "#d32f2f"
                      : urgencia === "proximo"
                      ? "#f57c00"
                      : "inherit";

                  return (
                    <MDBox
                      key={`${it?.refcliente}_${it?.refpagador}_${it?.refvendedor}_${idx}`}
                      px={2}
                      py={1.25}
                      sx={{
                        "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                      }}
                    >
                      <MDBox
                        display="flex"
                        alignItems="flex-start"
                        gap={1.25}
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                          handleClose();
                          onOpenInfo?.(it);
                        }}
                      >
                        <MDBox flex={1} minWidth={0}>
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: corNome, lineHeight: 1.25 }}
                          >
                            <strong>Cliente:</strong> {it?.cliente || "-"} ({it?.refcliente || "-"})
                            {it?.pagador ? (
                              <>
                                <br />
                                <strong>Pagador:</strong> {it.pagador} ({it?.refpagador || "-"})
                              </>
                            ) : null}
                          </MDTypography>

                          <MDBox mt={0.75}>
                            <MDTypography variant="caption" display="block">
                              <strong>Vendedor:</strong> {it?.vendedor || "-"} (
                              {it?.refvendedor || "-"})
                            </MDTypography>

                            <MDTypography variant="caption" display="block">
                              <strong>Última venda:</strong> {fmtDate(it?.data_ultima_venda)}
                            </MDTypography>

                            <MDTypography variant="caption" display="block">
                              <strong>Débito:</strong> {fmtMoney(it?.valor_debito)} €
                            </MDTypography>

                            <MDTypography variant="caption" display="block">
                              <strong>Débito +90d:</strong> {fmtMoney(it?.valor_debito_90mais)} €
                            </MDTypography>

                            <MDTypography variant="caption" display="block">
                              <strong>Último contacto:</strong>{" "}
                              {it?.data_ultimo_contacto
                                ? fmtDateTime(it.data_ultimo_contacto)
                                : "-"}
                            </MDTypography>

                            <MDTypography
                              variant="caption"
                              display="block"
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "100%",
                              }}
                              title={it?.obs_ultimo_contacto || ""}
                            >
                              <strong>Obs.:</strong> {it?.obs_ultimo_contacto || "-"}
                            </MDTypography>

                            <MDTypography
                              variant="caption"
                              display="block"
                              sx={{ color: corNome, fontWeight: urgencia ? "bold" : "regular" }}
                            >
                              <strong>Próx. agendamento:</strong>{" "}
                              {it?.data_proximo_agendamento
                                ? fmtDateTime(it.data_proximo_agendamento)
                                : "-"}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </MDBox>

                      {idx < itemsFiltrados.length - 1 ? <Divider sx={{ mt: 1.25 }} /> : null}
                    </MDBox>
                  );
                })
              )}
            </MDBox>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
